import {
  HANDLE_HIT_RADIUS,
  hitTestShape,
  moveShape,
  resizeShape,
  rotateShapeTo,
  shapeHandles,
  shapesBounds,
} from '@/Components/CRM/QualityReview/qaShapes';

/**
 * Маркер выделения под указателем: сначала ручка поворота, затем угол или конец отрезка.
 */
const handleAt = (shape, point, tolerance) => {
  const { points, rotate } = shapeHandles(shape);
  const radius = HANDLE_HIT_RADIUS + tolerance;

  if (rotate && Math.hypot(point.x - rotate.x, point.y - rotate.y) <= radius) {
    return { type: 'rotate' };
  }

  const index = points.findIndex(
    (handle) => Math.hypot(point.x - handle.x, point.y - handle.y) <= radius,
  );

  return index >= 0 ? { type: 'resize', index } : null;
};

/**
 * Состояние разметки: нарисованные фигуры, черновик под курсором и выделение.
 * События указателя превращает в операции — рисование, сдвиг, изменение размера, поворот.
 *
 * Тот же редактор живёт в QA-плагине виджета (__shared/plugins/QAReviewPlugin/ShapeEditor.js);
 * там поля приватные с префиксом `_`, здесь — обычные, как требует линтер проекта.
 */
export class ShapeEditor {
  shapes = [];

  draft = null;

  selectedId = null;

  action = null;

  nextId = 1;

  /** Фигура под выделением. */
  get selected() {
    return this.shapes.find((shape) => shape.id === this.selectedId) ?? null;
  }

  /** Всё, что нужно нарисовать: сохранённые фигуры и черновик под курсором. */
  get visibleShapes() {
    return [...this.shapes, this.draft].filter(Boolean);
  }

  get isEmpty() {
    return this.shapes.length === 0;
  }

  reset() {
    this.shapes = [];
    this.draft = null;
    this.selectedId = null;
    this.action = null;
  }

  undo() {
    this.shapes = this.shapes.slice(0, -1);
    this.selectedId = null;
    this.draft = null;
  }

  /** @returns {boolean} было ли что удалять */
  deleteSelected() {
    if (!this.selected) return false;

    this.shapes = this.shapes.filter((shape) => shape.id !== this.selectedId);
    this.selectedId = null;
    this.action = null;
    return true;
  }

  /** @returns {boolean} было ли снято выделение */
  deselect() {
    if (this.selectedId === null) return false;

    this.selectedId = null;
    this.action = null;
    return true;
  }

  /**
   * @param {{ x: number, y: number }} point
   * @param {{ tool: string, color: string, width: number, tolerance?: number }} options
   * @returns {boolean} нужно ли перерисовать холст
   */
  pointerDown(point, {
    tool, color, width, tolerance = 6,
  }) {
    const { selected } = this;

    if (selected) {
      const handle = handleAt(selected, point, tolerance);
      if (handle) {
        this.action = handle;
        return true;
      }
    }

    const hit = [...this.shapes].reverse()
      .find((shape) => hitTestShape(shape, point, tolerance)) ?? null;

    if (tool === 'select') {
      this.selectedId = hit?.id ?? null;
      this.action = hit ? { type: 'move', from: point } : null;
      return true;
    }

    // Рисующим инструментом фигуру всё равно можно подвинуть — но только выделенную,
    // иначе новый штрих поверх старой фигуры превращался бы в её перетаскивание.
    if (selected && hit && hit.id === selected.id) {
      this.action = { type: 'move', from: point };
      return true;
    }

    this.selectedId = null;
    this.draft = tool === 'pen'
      ? {
        type: 'pen', points: [point], color, width,
      }
      : {
        type: tool, start: point, end: point, color, width,
      };

    return true;
  }

  /**
   * @param {{ x: number, y: number }} point
   * @returns {boolean} нужно ли перерисовать холст
   */
  pointerMove(point) {
    if (this.action) return this.applyAction(point);

    if (this.draft) {
      if (this.draft.type === 'pen') this.draft.points.push(point);
      else this.draft.end = point;
      return true;
    }

    return false;
  }

  /**
   * @param {{ minSize?: number }} [options] порог, ниже которого клик не становится фигурой
   * @returns {boolean} нужно ли перерисовать холст
   */
  pointerUp({ minSize = 0 } = {}) {
    if (this.action) {
      this.action = null;
      return true;
    }

    if (!this.draft) return false;

    const bounds = shapesBounds([this.draft]);
    const { draft } = this;
    this.draft = null;

    if (!bounds || Math.max(bounds.w, bounds.h) <= minSize) return true;

    const shape = { ...draft, id: this.nextId };
    this.nextId += 1;
    this.shapes = [...this.shapes, shape];
    // Свежую фигуру сразу выделяем: её чаще всего и нужно повернуть или поправить.
    this.selectedId = shape.id;

    return true;
  }

  /**
   * Курсор подсказывает, что произойдёт по нажатию в этой точке.
   *
   * @param {{ x: number, y: number }} point
   * @param {{ tool: string, tolerance?: number }} options
   * @returns {string}
   */
  cursorAt(point, { tool, tolerance = 6 }) {
    const { selected } = this;

    if (selected) {
      const handle = handleAt(selected, point, tolerance);
      if (handle?.type === 'rotate') return 'grab';
      if (handle?.type === 'resize') return 'nwse-resize';
      if (hitTestShape(selected, point, tolerance)) return 'move';
    }

    if (tool !== 'select') return 'crosshair';

    return this.shapes.some((shape) => hitTestShape(shape, point, tolerance)) ? 'move' : 'default';
  }

  /**
   * @param {{ x: number, y: number }} point
   * @returns {boolean}
   */
  applyAction(point) {
    const { selected } = this;
    if (!selected) {
      this.action = null;
      return false;
    }

    if (this.action.type === 'rotate') {
      this.replaceSelected(rotateShapeTo(selected, point));
      return true;
    }

    if (this.action.type === 'resize') {
      this.replaceSelected(resizeShape(selected, this.action.index, point));
      return true;
    }

    const dx = point.x - this.action.from.x;
    const dy = point.y - this.action.from.y;
    this.action.from = point;
    this.replaceSelected(moveShape(selected, dx, dy));

    return true;
  }

  /**
   * @param {*} shape
   * @returns {void}
   */
  replaceSelected(shape) {
    this.shapes = this.shapes.map((current) => (current.id === this.selectedId ? shape : current));
  }
}
